import { useMemo } from 'react';

import { EWebtraderResetPasswordType } from '@aqx-core/common/api/config';
import { useCompanyProfile } from '@aqx-core/common/services/company';
import { validateCompanyAccess } from '@aqx-core/common/utils/company-access';
import { isValidUrl } from '@aqx-core/common/validators';

export const useAuthRoutes = () => {
  const companyProfile = useCompanyProfile();
  const { companyRegistrationUrl, crmForgotPasswordType, webtraderResetPasswordType, webtraderDemoRegistrationUrl } =
    companyProfile || {};

  const {
    hasCompanyLoginConfig,
    hasDemoAccountCreation,
    hasMT4DemoLoginConfig,
    hasMT5DemoLoginConfig,
    hasCentroidDemoLoginConfig,
    hasMT4LiveLoginConfig,
    hasMT5LiveLoginConfig,
    hasCentroidLiveLoginConfig,
    hasResetPasswordAccess,
    hasForgotPasswordAccess,
    hasExperienceAccess
  } = useMemo(() => {
    return validateCompanyAccess(companyProfile);
  }, [companyProfile]);

  // ** LIVE **
  // For LIVE ID tabs (MT4/MT5/Centroid), there is no internal sign up form.
  // The button visibility is determined by the registration URL in use-login-sign-up-action.
  const hasLiveLoginConfig = useMemo(() => {
    return hasMT4LiveLoginConfig || hasMT5LiveLoginConfig || hasCentroidLiveLoginConfig;
  }, [hasMT4LiveLoginConfig, hasMT5LiveLoginConfig, hasCentroidLiveLoginConfig]);

  // Always return true if login config exists, actual button visibility depends on registrationUrl
  const hasLiveSignUpButton = useMemo(() => {
    return hasLiveLoginConfig;
  }, [hasLiveLoginConfig]);

  const hasLiveForgotPasswordButton = useMemo(() => {
    return hasLiveLoginConfig && hasForgotPasswordAccess;
  }, [hasForgotPasswordAccess, hasLiveLoginConfig]);

  const hasLiveForgotPasswordRoute = useMemo(() => {
    return hasLiveForgotPasswordButton && webtraderResetPasswordType === EWebtraderResetPasswordType.INTEGRATION;
  }, [hasLiveForgotPasswordButton, webtraderResetPasswordType]);

  // ** DEMO **
  // For demo, there is only sign up for MT4, MT5 demo or Centroid demo.
  // There is no sign up UI/redirection for experience demo.
  // Experience demo sign up is handled from AQX website
  const hasDemoLoginConfig = useMemo(() => {
    return hasMT4DemoLoginConfig || hasMT5DemoLoginConfig || hasCentroidDemoLoginConfig;
  }, [hasMT4DemoLoginConfig, hasMT5DemoLoginConfig, hasCentroidDemoLoginConfig]);

  // Priority: hasExperienceAccess > hasDemoAccountCreation > webtraderDemoRegistrationUrl
  // 1. hasExperienceAccess - First flag to show/hide the button (handled externally on AQX website)
  // 2. hasDemoAccountCreation - BO config "Show Demo Account Creation" to show internal form
  // 3. webtraderDemoRegistrationUrl - RA config demo URL to show external link
  const { hasButton: hasDemoSignUpButton, hasRoute: hasDemoSignUpRoute } = useMemo(() => {
    if (hasExperienceAccess) {
      return { hasButton: false, hasRoute: false };
    }

    if (hasDemoAccountCreation) {
      return { hasButton: true, hasRoute: true };
    }

    if (webtraderDemoRegistrationUrl) {
      return { hasButton: true, hasRoute: false };
    }

    return { hasButton: false, hasRoute: false };
  }, [hasDemoAccountCreation, hasExperienceAccess, webtraderDemoRegistrationUrl]);

  // For demo, only experience demo has reset password
  // If have demo reset password, have both route and button for it
  const hasDemoResetPassword = useMemo(() => {
    return hasDemoLoginConfig && hasExperienceAccess;
  }, [hasExperienceAccess, hasDemoLoginConfig]);

  // ** CRM **
  const hasCrmSignUpButton = useMemo(() => {
    // Always show sign up button when company login config exists
    // The action (internal form vs external URL) is determined by hasCrmSignUpRoute
    return hasCompanyLoginConfig;
  }, [hasCompanyLoginConfig]);

  const hasCrmSignUpRoute = useMemo(() => {
    // Use internal form when:
    // 1. companyRegistrationUrl is 'form'
    // 2. companyRegistrationUrl is empty/invalid (fallback to internal form)
    if (!hasCompanyLoginConfig) {
      return false;
    }
    const isForm = companyRegistrationUrl?.toLocaleLowerCase() === 'form';
    const hasValidUrl = !!companyRegistrationUrl && isValidUrl(companyRegistrationUrl);
    // Use internal route when it's 'form' OR when there's no valid external URL
    return isForm || !hasValidUrl;
  }, [companyRegistrationUrl, hasCompanyLoginConfig]);

  const hasCrmForgotPasswordButton = useMemo(
    () => crmForgotPasswordType && hasResetPasswordAccess && hasCompanyLoginConfig,
    [crmForgotPasswordType, hasCompanyLoginConfig, hasResetPasswordAccess]
  );

  const hasCrmForgotPasswordRoute = useMemo(
    () => hasCrmForgotPasswordButton && crmForgotPasswordType === EWebtraderResetPasswordType.INTEGRATION,
    [crmForgotPasswordType, hasCrmForgotPasswordButton]
  );

  return {
    hasLiveSignUpButton,
    hasLiveForgotPasswordButton,
    hasLiveForgotPasswordRoute,
    hasDemoSignUpButton,
    hasDemoSignUpRoute,
    hasDemoResetPassword,
    hasCrmSignUpButton,
    hasCrmSignUpRoute,
    hasCrmForgotPasswordButton,
    hasCrmForgotPasswordRoute,
    companyProfile
  };
};
